import { useMemo, useState } from 'react';
import { CardWrapper, CardHeader, CardSection } from 'foundations-components/transitional/components/Card';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { PaginationControls } from './PaginationControls';
import { useIsMobileView } from '../hooks/useWindowSize';
import { ROUTES } from './Routes';
import { subscriptionStatusToStatusBadgeMessage, subscriptionStatusToStatusBadgeUse } from '../utils/typeAdapters';
import { Button } from 'foundations-components/transitional/components/Button';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function SubscriptionTile({
  subscription
}) {
  var _subscription$name;
  const isMobileView = useIsMobileView();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const updatePaymentMethodUrlRoute = useMemo(() => {
    if (!subscription.updateRequestURL) {
      return '';
    }
    return `${ROUTES.updatePaymentMethod}?url=${encodeURIComponent(subscription.updateRequestURL)}`;
  }, [subscription.updateRequestURL]);
  const statusBadgeToUse = useMemo(() => {
    if (!subscription.status || !(subscription.status in subscriptionStatusToStatusBadgeUse)) {
      return;
    }
    return subscriptionStatusToStatusBadgeUse[subscription.status];
  }, [subscription.status]);
  const statusBadgeMessage = useMemo(() => {
    if (!subscription.status || !(subscription.status in subscriptionStatusToStatusBadgeMessage)) {
      return;
    }
    return subscriptionStatusToStatusBadgeMessage[subscription.status];
  }, [subscription.status]);
  return /*#__PURE__*/_jsxs(Tile, {
    children: [/*#__PURE__*/_jsx(TileSection, {
      paddingInline: "700",
      paddingBlock: "600",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        rowGap: "400",
        children: [/*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-200",
              children: (_subscription$name = subscription.name) !== null && _subscription$name !== void 0 ? _subscription$name : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.unnamedSubscription"
              })
            }), !!statusBadgeToUse && !!statusBadgeMessage && /*#__PURE__*/_jsx(Badge, {
              use: statusBadgeToUse,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: statusBadgeMessage
              })
            })]
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          gap: "800",
          flexDirection: isMobileView ? 'column' : 'row',
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.subscriptionDates"
              })
            }), /*#__PURE__*/_jsxs(List, {
              children: [/*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.startDate",
                  options: {
                    date: /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.startDate,
                      format: "LL"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.endDate",
                  options: {
                    date: typeof subscription.endDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.endDate,
                      format: "LL"
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.nextPayment",
                  options: {
                    date: typeof subscription.nextPaymentDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                      value: subscription.nextPaymentDate,
                      format: "LL"
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              })]
            })]
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "body-125",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.billingDetails"
              })
            }), /*#__PURE__*/_jsxs(List, {
              children: [/*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.amount",
                  options: {
                    amount: typeof subscription.nextPaymentAmount === 'number' ? /*#__PURE__*/_jsx(FormattedIntlCurrency, {
                      value: subscription.nextPaymentAmount,
                      currencyCode: subscription.currencyCode
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              }), /*#__PURE__*/_jsx(ListItem, {
                children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "subscriptions.paymentMethod.label",
                  options: {
                    method: subscription.paymentMethodDetails ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "subscriptions.paymentMethod.last4",
                      options: {
                        type: subscription.paymentMethodDetails.type,
                        last4: subscription.paymentMethodDetails.last4
                      }
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "common.unknown"
                    })
                  }
                })
              })]
            })]
          })]
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "600",
          children: /*#__PURE__*/_jsxs(Flex, {
            gap: "400",
            flexDirection: isMobileView ? 'column' : 'row',
            children: [/*#__PURE__*/_jsx(RouterButton, {
              use: "primary",
              size: "small",
              paddingInline: "600",
              to: updatePaymentMethodUrlRoute,
              disabled: !subscription.updateRequestURL,
              children: /*#__PURE__*/_jsx(Text, {
                typography: "heading-50",
                textWrap: "wrap",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.updatePaymentMethod"
                })
              })
            }), /*#__PURE__*/_jsx(Button, {
              use: "transparent",
              size: "small",
              paddingInline: "600",
              onClick: () => setShowCancelModal(true),
              children: /*#__PURE__*/_jsx(Text, {
                typography: "heading-50",
                textWrap: "wrap",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "subscriptions.requestToCancel"
                })
              })
            })]
          })
        })]
      })
    }), showCancelModal && /*#__PURE__*/_jsxs(Modal, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.requestToCancel"
          })
        }), /*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: () => setShowCancelModal(false)
        })]
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          rowGap: "400",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            as: "strong",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.cancellationReasonLabel"
            })
          }), /*#__PURE__*/_jsx(Text, {
            typography: "body-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "subscriptions.cancellationReasonDescription"
            })
          }), /*#__PURE__*/_jsx(TextArea, {
            value: cancellationReason,
            onChange: e => setCancellationReason(e.target.value),
            placeholder: I18n.text('subscriptions.cancellationReasonPlaceholder')
          })]
        })
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(Button, {
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.submitRequest"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          onClick: () => setShowCancelModal(false),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "subscriptions.close"
          })
        })]
      })]
    })]
  });
}
export function SubscriptionsCard({
  subscriptions = [],
  pagination
}) {
  const isMobileView = useIsMobileView();
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [/*#__PURE__*/_jsx(CardHeader, {
      padding: isMobileView ? '700' : undefined,
      title: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "subscriptions.title"
        }), pagination.loading && /*#__PURE__*/_jsx(LoadingSpinner, {
          margin: "0",
          size: "extra-small"
        })]
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: isMobileView ? '700' : undefined,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        rowGap: "300",
        children: /*#__PURE__*/_jsx(PaginationControls, {
          isEmpty: subscriptions.length === 0,
          pagination: pagination,
          children: subscriptions.map(subscription => /*#__PURE__*/_jsx(SubscriptionTile, {
            subscription: subscription
          }, subscription.subscriptionId))
        })
      })
    })]
  });
}